/** Section ids, order, headings and render-time numbering for the OIO draft (contract v3). The page, outline and DOCX all read it. */
export const HEAD_SECTIONS = ["preamble", "subject", "background", "submissions", "hearing"] as const
/** After the findings. `maintainability` is placed before them (see canonicalIds). */
export const TAIL_SECTIONS = ["order", "closing"] as const

const FINDINGS = "findings."

/** The group heading drawn once above threshold, maintainability and every finding. */
export const DISCUSSION_GROUP = "DISCUSSION AND FINDINGS"
const PRELIMINARY = "PRELIMINARY ISSUES"

/** Allegation titles from GET /draft (`titles`), keyed by section id ("findings.A1"). */
export type SectionTitles = Record<string, string> | undefined

const FIXED: Record<string, string> = {
  preamble: "PREAMBLE",
  background: "BRIEF FACTS OF THE CASE",
  submissions: "WRITTEN SUBMISSIONS",
  hearing: "PERSONAL HEARING",
  order: "ORDER",
}

export interface SectionHeading {
  /** Canonical heading text; empty when the section has no heading of its own. */
  text: string
  /** 1 for a main heading, 2 for a sub-heading ("Issue n: ...", "PRELIMINARY ISSUES"). */
  level: 1 | 2
  /** Set on every section inside "DISCUSSION AND FINDINGS"; the group heading is drawn once above the first of them. */
  group?: string
  /** PREAMBLE and ORDER are centred and underlined. */
  center?: boolean
}

/** "Issue n: <title>" for findings.X, using the 1-based position among the findings; "Issue n" when the title is missing. */
export function issueHeading(id: string, ids: readonly string[], titles?: SectionTitles): string {
  const n = ids.filter((x) => x.startsWith(FINDINGS)).indexOf(id) + 1
  const title = titles?.[id]?.trim()
  return title ? `Issue ${n}: ${title}` : `Issue ${n}`
}

const IN_GROUP = (id: string) => id === "threshold" || id === "maintainability" || id.startsWith(FINDINGS)
const UNNUMBERED_SECTIONS = new Set(["preamble", "subject"])

/**
 * The order's outline, as a signed order numbers it (and as oio_service's compile does: "5. DISCUSSION AND FINDINGS"):
 * every main heading takes the next integer, the discussion group takes one integer for all of it, and each titled
 * sub-section inside it (the preliminary issues, each issue) is n.m. `unit` is the prefix a section's paragraphs hang
 * under ("1" -> 1.1, 1.2; "4.2" -> 4.2.1); maintainability shares the preliminary issues' unit when threshold is there,
 * and the closing continues the Order's. Preamble and subject are not numbered.
 */
export function outline(ids: readonly string[], has: (id: string) => boolean = () => true): { unit: Map<string, string>; group: number | null } {
  const unit = new Map<string, string>()
  let n = 0
  let group: number | null = null
  let sub = 0
  let last = ""
  for (const id of ids) {
    // A section with no text yet takes no number, so the numbers never jump (the .docx leaves it out).
    if (UNNUMBERED_SECTIONS.has(id) || !has(id)) continue
    if (IN_GROUP(id)) {
      if (group === null) group = ++n
      if (id === "maintainability" && ids.includes("threshold") && unit.has("threshold")) unit.set(id, unit.get("threshold")!)
      else unit.set(id, `${group}.${++sub}`)
    } else if (FIXED[id]) {
      unit.set(id, String(++n))
    } else {
      // The closing (and anything unknown) runs on under the section before it.
      unit.set(id, last || String(++n))
    }
    last = unit.get(id)!
  }
  return { unit, group }
}

/** Canonical headings for the given section ids, numbered as the order numbers them. Sections without a heading (subject, closing) get an empty text. */
export function sectionHeadings(ids: readonly string[], titles?: SectionTitles, has?: (id: string) => boolean): Record<string, SectionHeading> {
  const { unit, group } = outline(ids, has)
  const discussion = group !== null ? `${group}. ${DISCUSSION_GROUP}` : DISCUSSION_GROUP
  const out: Record<string, SectionHeading> = {}
  for (const id of ids) {
    const at = unit.get(id)
    const num = (sep: string) => (at ? `${at}${sep}` : "")
    if (id === "threshold") out[id] = { text: `${num(" ")}${PRELIMINARY}`, level: 2, group: discussion }
    else if (id === "maintainability") {
      // Shares the "PRELIMINARY ISSUES" heading with threshold when both are present.
      out[id] = { text: ids.includes("threshold") && (!has || has("threshold")) ? "" : `${num(" ")}${PRELIMINARY}`, level: 2, group: discussion }
    } else if (id.startsWith(FINDINGS)) out[id] = { text: `${num(" ")}${issueHeading(id, ids, titles)}`, level: 2, group: discussion }
    else if (id === "preamble") out[id] = { text: FIXED[id], level: 1, center: true }
    else if (id === "order") out[id] = { text: `${num(". ")}${FIXED[id]}`, level: 1, center: true }
    else out[id] = { text: FIXED[id] ? `${num(". ")}${FIXED[id]}` : "", level: 1 }
  }
  return out
}

const LABELS: Record<string, string> = {
  preamble: "Preamble",
  subject: "Subject",
  background: "Brief facts of the case",
  submissions: "Written submissions",
  hearing: "Personal hearing",
  threshold: "Threshold issues",
  maintainability: "Jurisdiction and maintainability",
  order: "Order",
  closing: "Closing",
}

/** Sentence-case label for outline links, aria labels and dialogs ("Issue 1: Inter-state supplies"). */
export function sectionLabel(id: string, ids?: readonly string[], titles?: SectionTitles): string {
  if (id.startsWith(FINDINGS)) return issueHeading(id, ids ?? [id], titles)
  return LABELS[id] ?? id
}

/**
 * Canonical order (contract v3): preamble, subject, background, submissions, hearing, threshold (only when present),
 * maintainability, findings.* in id order, order, closing, then anything unknown.
 * The fixed sections are always listed, so an undrafted section shows as a placeholder.
 */
export function canonicalIds(present: Iterable<string>): string[] {
  const have = new Set(present)
  const findings = [...have]
    .filter((id) => id.startsWith(FINDINGS))
    .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))
  const known = new Set<string>([...HEAD_SECTIONS, "threshold", "maintainability", ...findings, ...TAIL_SECTIONS])
  const unknown = [...have].filter((id) => !known.has(id)).sort()
  return [
    ...HEAD_SECTIONS,
    ...(have.has("threshold") ? ["threshold"] : []),
    "maintainability",
    ...findings,
    ...TAIL_SECTIONS,
    ...unknown,
  ]
}

/** Removes a leading markdown heading line (the backend strips it too; this guards older saved text). */
export function stripLeadingHeading(markdown: string): string {
  return markdown.replace(/^\s*#{1,6}[ \t]+[^\n]*(?:\n|$)/, "").replace(/^\s*\n/, "")
}

export const CAPTION_PLACEHOLDERS = ["[OIO No.]", "[DIN]"] as const

/** Today as dd.mm.yyyy, the caption date when no order date is known. */
export function todayDmy(now: Date = new Date()): string {
  const p = (n: number) => String(n).padStart(2, "0")
  return `${p(now.getDate())}.${p(now.getMonth() + 1)}.${now.getFullYear()}`
}

/* ------------------------------------------------------------------------------------------------
 * Render-time paragraph numbering
 * Every paragraph hangs under its heading's number (see `outline`): BRIEF FACTS 1 -> 1.1, 1.2; an issue 4.2 -> 4.2.1.
 * Preamble and subject are not numbered. List items directly under a numbered paragraph get its label plus .1, .2;
 * a list with no paragraph above it numbers its items as paragraphs. Headings, tables, quotes, lettered clauses "(i)" and the
 * closing's tail (DIN, To block, copy-to list, signature) are never numbered. Typed leading numbers are stripped.
 * ---------------------------------------------------------------------------------------------- */

export type BlockKind = "heading" | "table" | "quote" | "list" | "para"

export interface MdBlock {
  kind: BlockKind
  /** para/quote/heading/table: the source lines. list: one entry per top-level item (indented continuation lines joined with "\n"). */
  lines: string[]
}

export interface PlannedBlock extends MdBlock {
  /** One label per rendered target: a single entry for a para/heading/table/quote, one per item for a list. null = unnumbered. */
  labels: (string | null)[]
  /** Numbered paragraphs strip typed numbers; false once the closing tail starts (the copy-to list keeps its own numbers). */
  tail: boolean
}

const LIST_ITEM = /^(\s*)([-*•]|\d+[.)])\s+(.*)$/
const TAIL_MARKER = /^(\*\*)?(DIN\s*:|To\s*[:,]?\s*(\*\*)?$|Copy\s+to\b|\[[^\]]+\]\s*$)/i
const LETTERED = /^\(([a-z]|[ivxlc]+)\)\s/i
const TYPED_NUMBER = /^(\d{1,3}(?:\.\d+)+[.)]?|\d{1,3}[.)])\s+/

/** Removes a typed leading number such as "6.", "5.2." or "12)" (bodies should carry none). */
export function stripTypedNumber(text: string): string {
  return text.replace(TYPED_NUMBER, "")
}

/** Splits section markdown into the blocks the editor renders: paragraphs, lists, tables, quotes and headings. */
export function splitBlocks(markdown: string): MdBlock[] {
  const lines = markdown.replace(/\r\n?/g, "\n").split("\n")
  const blocks: MdBlock[] = []
  let para: string[] = []
  const flush = () => {
    if (para.length) blocks.push({ kind: "para", lines: para })
    para = []
  }
  let i = 0
  while (i < lines.length) {
    const line = lines[i]
    const t = line.trim()
    if (!t) { flush(); i++; continue }
    if (/^#{1,6}\s/.test(t)) { flush(); blocks.push({ kind: "heading", lines: [t] }); i++; continue }
    if (t.startsWith("|")) {
      flush()
      const rows: string[] = []
      while (i < lines.length && lines[i].trim().startsWith("|")) rows.push(lines[i++].trim())
      blocks.push({ kind: "table", lines: rows })
      continue
    }
    if (t.startsWith(">")) {
      flush()
      const rows: string[] = []
      while (i < lines.length && lines[i].trim().startsWith(">")) rows.push(lines[i++].trim().replace(/^>\s?/, ""))
      blocks.push({ kind: "quote", lines: rows })
      continue
    }
    const m = line.match(LIST_ITEM)
    if (m && m[1].length < 2) {
      flush()
      const items: string[] = []
      while (i < lines.length) {
        const cur = lines[i]
        if (!cur.trim()) {
          // A blank line inside a loose list: the list goes on only if another top-level item follows.
          let j = i
          while (j < lines.length && !lines[j].trim()) j++
          const next = lines[j]?.match(LIST_ITEM)
          if (next && next[1].length < 2) { i = j; continue }
          break
        }
        const im = cur.match(LIST_ITEM)
        if (im && im[1].length < 2) items.push(im[3].trim())
        else if (/^\s{2,}\S/.test(cur) && items.length) items[items.length - 1] += `\n${cur.trim()}`
        else break
        i++
      }
      blocks.push({ kind: "list", lines: items })
      continue
    }
    para.push(t)
    i++
  }
  flush()
  return blocks
}

/** Numbers every paragraph under its heading's number (1.1, 1.2; 4.2.1 inside an issue). `sections` must be in canonical order. */
export function planNumbering(sections: ReadonlyArray<{ id: string; markdown: string }>): Map<string, PlannedBlock[]> {
  const out = new Map<string, PlannedBlock[]>()
  const text = new Map(sections.map((x) => [x.id, stripLeadingHeading(x.markdown).trim()]))
  const { unit } = outline(sections.map((x) => x.id), (id) => !!text.get(id))
  const count = new Map<string, number>()
  const next = (u: string) => {
    const k = (count.get(u) ?? 0) + 1
    count.set(u, k)
    return `${u}.${k}`
  }
  for (const { id, markdown } of sections) {
    const u = unit.get(id)
    let current: string | null = null // the numbered paragraph that list items hang under
    let sub = 0
    let tail = false
    const planned: PlannedBlock[] = []
    for (const block of splitBlocks(stripLeadingHeading(markdown))) {
      if (!u) {
        planned.push({ ...block, labels: block.kind === "list" ? block.lines.map(() => null) : [null], tail: false })
        continue
      }
      if (block.kind === "para") {
        if (!tail && TAIL_MARKER.test(block.lines[0])) tail = true
        if (tail || LETTERED.test(block.lines[0])) {
          planned.push({ ...block, labels: [null], tail })
        } else {
          current = next(u)
          sub = 0
          planned.push({ ...block, labels: [current], tail: false })
        }
      } else if (block.kind === "list") {
        if (tail) planned.push({ ...block, labels: block.lines.map(() => null), tail })
        else if (current !== null) planned.push({ ...block, labels: block.lines.map(() => `${current}.${++sub}`), tail: false })
        else planned.push({ ...block, labels: block.lines.map(() => next(u)), tail: false })
      } else {
        current = null
        planned.push({ ...block, labels: [null], tail })
      }
    }
    out.set(id, planned)
  }
  return out
}
